import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Checkbox,
  Button,
  CircularProgress,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { gapi } from "gapi-script";
import { useQuery, useMutation } from '@apollo/client';
import { UserContext } from '../../auth/auth-context';
import { GoogleCalendarService } from '../../../services/calendar/googleCalendarService';
import { CHECK_GOOGLE_CALENDAR_INTEGRATION } from '../../../graphql/queries';
import { DELETE_GOOGLE_CALENDAR_INTEGRATION, EXCHANGE_GOOGLE_CALENDAR_CODE } from '../../../graphql/mutations';

const CalendarIntegration = ({ isDisabled = false }) => {
  const { providerUser } = useContext(UserContext);

  const [checkedServices, setCheckedServices] = useState({
    google: false,
    outlook: false,
  });
  const [integratedServices, setIntegratedServices] = useState({
    google: false,
    outlook: false,
  });

  // State to show connection status and loading indicator for Google
  const [googleStatus, setGoogleStatus] = useState('Not connected');
  const [loadingStatus, setLoadingStatus] = useState(false);
  const oauthHandledRef = useRef(false);

  const { data: checkIntegrationData, refetch } = useQuery(CHECK_GOOGLE_CALENDAR_INTEGRATION, {
    variables: { staffId: providerUser?.staffId },
    skip: !providerUser?.staffId,
  });

  const [deleteGoogleIntegration] = useMutation(DELETE_GOOGLE_CALENDAR_INTEGRATION);
  const [exchangeGoogleCode] = useMutation(EXCHANGE_GOOGLE_CALENDAR_CODE);
  const isPopupMode = new URLSearchParams(window.location.search).get("popup") === "1";

  // Update integration status from backend query
  useEffect(() => {
    if (checkIntegrationData?.checkGoogleCalendarIntegration !== undefined) {
      const isIntegrated = checkIntegrationData.checkGoogleCalendarIntegration;
      setIntegratedServices(prev => ({
        ...prev,
        google: isIntegrated,
      }));
      setCheckedServices(prev => ({
        ...prev,
        google: isIntegrated,
      }));
      setGoogleStatus(isIntegrated ? 'Connected to Google Calendar' : 'Not connected');
    }
  }, [checkIntegrationData]);

  // Initialize Google API client
  useEffect(() => {
    const initClient = () => {
      gapi.load("client:auth2", () => {
        gapi.client
          .init({
            clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            scope: "https://www.googleapis.com/auth/calendar.readonly",
          })
          .then(() => {
            gapi.client.load("calendar", "v3", () => { 
            });
          });
      });
    };
    initClient();
  }, []);

  // Handle OAuth callback from Google and persist integration.
  useEffect(() => {
    const handleOAuthCallback = async () => {
      if (oauthHandledRef.current) return;
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      if (!code || !providerUser?.staffId) return;

      oauthHandledRef.current = true;
      setLoadingStatus(true);
      setGoogleStatus('Connecting to Google Calendar...');
      try {
        const { data } = await exchangeGoogleCode({
          variables: { code, staffId: Number(providerUser.staffId) },
        });
        if (data?.exchangeGoogleCalendarCode?.success) {
          localStorage.removeItem('calendarAuthPending');
          setGoogleStatus('Successfully connected to Google Calendar.');
          setIntegratedServices((prev) => ({ ...prev, google: true }));
          setCheckedServices((prev) => ({ ...prev, google: true }));
          await refetch({ staffId: Number(providerUser.staffId) });
          const successPayload = { type: 'google_calendar_connected', staffId: Number(providerUser.staffId) };
          if (window.parent && window.parent !== window) {
            window.parent.postMessage(successPayload, window.location.origin);
          }
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(successPayload, window.location.origin);
            window.setTimeout(() => window.close(), 250);
          } else if (!isPopupMode) {
            // When opened in full page mode, return user to calendar after successful connection.
            window.location.assign('/biz/calendar');
          }
        } else {
          setGoogleStatus('Failed to authenticate with Google Calendar.');
        }
      } catch (error) {
        console.error('Error during OAuth callback:', error);
        setGoogleStatus('Failed to fetch calendar events.');
      } finally {
        setLoadingStatus(false);
        const cleanUrl = `${window.location.pathname}${window.location.hash || ''}`;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    };

    handleOAuthCallback();
  }, [exchangeGoogleCode, providerUser?.staffId, refetch]);


  // Toggle Google Calendar integration: connect or disconnect
  const handleCheckboxChange = async (service) => {
    if (service === 'google') {
      if (!checkedServices.google) {
        // Start OAuth flow
        const googleCalendarService = new GoogleCalendarService();
        try {
          // Use the registered redirect URI to avoid Google OAuth 403 mismatch.
          const authUrl = googleCalendarService.getAuthUrl();
          localStorage.setItem('calendarAuthPending', 'true');
          window.location.href = authUrl;
        } catch (error) {
          console.error('Error initiating Google OAuth:', error);
          return;
        }
      } else {
        // Disconnect Google Calendar
        try {
          setLoadingStatus(true);
          await deleteGoogleIntegration({ variables: { staffId: Number(providerUser?.staffId) } });
          await refetch({ staffId: Number(providerUser?.staffId) });
          setIntegratedServices((prev) => ({ ...prev, google: false }));
          setCheckedServices((prev) => ({ ...prev, google: false }));
          setGoogleStatus('Disconnected from Google Calendar');
        } catch (error) {
          console.error('Error disconnecting Google Calendar:', error);
          setGoogleStatus('Failed to disconnect Google Calendar.');
        } finally {
          setLoadingStatus(false);
        }
      }
    }
  };

  return (
    <Container>
      <Box mt={4}>
        <Typography variant="h4" gutterBottom>
          Calendar Integration
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Paper elevation={3} style={{ padding: "16px", textAlign: "center" }}>
              <Checkbox
                disabled={isDisabled}
                checked={checkedServices.google}
                onChange={() => handleCheckboxChange("google")}
              />
              <CalendarTodayIcon fontSize="large" />
              <Typography variant="h6">Google Calendar</Typography>
              {loadingStatus ? (
                <CircularProgress size={20} />
              ) : (
                <Typography variant="body2" color={integratedServices.google ? 'success.main' : 'textSecondary'}>
                  {googleStatus}
                </Typography>
              )}
              <Button variant="outlined" onClick={() => handleCheckboxChange("google")} sx={{ mt: 2, width: { xs: "100%", sm: "auto" } }} disabled={isDisabled}>
                {integratedServices.google ? "Disconnect" : "Connect"}
              </Button>
            </Paper>
          </Grid>
          {/* <Grid item xs={6}>
            <Paper elevation={3} style={{ padding: "16px", textAlign: "center" }}>
              <Checkbox
                checked={checkedServices.outlook}
                onChange={() => handleCheckboxChange("outlook")}
              />
              <Typography variant="h6">Outlook Calendar</Typography>
              <Button variant="contained" disabled={!checkedServices.outlook}>
                Enable
              </Button>
            </Paper>
          </Grid> */}
        </Grid>
      </Box>
    </Container>
  );
};

export default CalendarIntegration;
